import { useRef, useState, ReactNode } from 'react'
import { toPng } from 'html-to-image'
import { Image as ImageIcon, Info } from 'lucide-react'

export type Kind = 'ok' | 'issue' | 'excluded'
export type CellState = 'ok' | 'issue' | 'missing' | 'na' | 'excluded' | 'future'
export interface Status { key: string; label: string; color: string; kind: Kind }
export interface StatusSet { id: string; name: string; statuses: Status[] }
export type Frequency = 'DAILY' | 'WEEKLY' | 'MONTHLY'
export const FREQ_LABEL: Record<Frequency, string> = { DAILY: 'รายวัน', WEEKLY: 'รายสัปดาห์', MONTHLY: 'รายเดือน' }
export const GROUP_KIND_LABEL: Record<string, string> = { GENERAL: 'ทั่วไป', SERVER: 'Server (คำนวณ Uptime)', BACKUP: 'Backup (คำนวณ Success rate)' }
export const KIND_LABEL: Record<Kind, string> = { ok: 'ผ่าน', issue: 'ผิดปกติ', excluded: 'ยกเลิก/ปิด (ไม่นับ)' }

export interface Rules { completeness: string; uptime: string; backup: string; openIssues: string }

// State colours are always paired with a glyph + text so the meaning never depends on colour alone.
export const STATE_STYLE: Record<CellState, { bg: string; fg: string; glyph: string; label: string }> = {
  ok:       { bg: '#16a34a', fg: '#ffffff', glyph: '✓', label: 'ตรวจแล้วผ่าน' },
  issue:    { bg: '#dc2626', fg: '#ffffff', glyph: '▲', label: 'ตรวจแล้วผิดปกติ' },
  missing:  { bg: '#fde68a', fg: '#92400e', glyph: '○', label: 'ยังไม่ได้ตรวจ' },
  na:       { bg: '#e5e7eb', fg: '#9ca3af', glyph: '–', label: 'ไม่เกี่ยวข้อง' },
  excluded: { bg: '#94a3b8', fg: '#ffffff', glyph: '⊘', label: 'ยกเลิก/ปิด (ไม่นับ)' },
  future:   { bg: '#f9fafb', fg: '#d1d5db', glyph: '', label: 'ยังไม่ถึงเวลา' },
}

export const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
export const fmtMonth = (ym: string) => `${THAI_MONTHS[parseInt(ym.slice(5, 7)) - 1]} ${ym.slice(2, 4)}`
export const fmtDay = (ymd: string) => `${parseInt(ymd.slice(8, 10))} ${THAI_MONTHS[parseInt(ymd.slice(5, 7)) - 1]}`

export const errMsg = (e: any) => e?.response?.data?.message || e?.message || 'เกิดข้อผิดพลาด'

export function downloadDataUrl(url: string, filename: string) {
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
}

/** Card with title, a rule tooltip (ⓘ) and a "save as PNG" button — used by every dashboard chart. */
export function ChartCard({ title, rule, children, className = '', png = true }: {
  title: string; rule?: string; children: ReactNode; className?: string; png?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)
  const save = async () => {
    if (!ref.current) return
    setBusy(true)
    try {
      const url = await toPng(ref.current, { backgroundColor: '#ffffff', pixelRatio: 2, filter: n => !(n as HTMLElement).dataset?.noExport })
      downloadDataUrl(url, `${title.replace(/[^\w฀-๿]+/g, '_')}.png`)
    } finally { setBusy(false) }
  }
  return (
    <div ref={ref} className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-4 ${className}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-1.5">
          {title}
          {rule && <span title={rule} className="text-gray-400 cursor-help"><Info size={13}/></span>}
        </h3>
        {png && (
          <button data-no-export="1" onClick={save} disabled={busy} title="บันทึกกราฟเป็น PNG"
            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0 disabled:opacity-40">
            <ImageIcon size={14}/>
          </button>
        )}
      </div>
      {rule && <p className="text-[11px] text-gray-400 mb-2 leading-snug">{rule}</p>}
      {children}
    </div>
  )
}

export function Legend({ items }: { items: { glyph: string; label: string; bg: string; fg: string }[] }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
      {items.map(i => (
        <span key={i.label} className="inline-flex items-center gap-1">
          <span className="w-4 h-4 rounded text-[10px] leading-4 text-center font-bold" style={{ background: i.bg, color: i.fg }}>{i.glyph}</span>{i.label}
        </span>
      ))}
    </div>
  )
}
